// src/modulos/Dashboard/dashboards/LaboratorioDashboard.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { db } from '../../firebaseConfig';
import {
    collection, query, getDocs, where, orderBy, limit, onSnapshot,
    doc, getDoc, updateDoc, serverTimestamp, Timestamp, writeBatch,
    deleteDoc, runTransaction
} from 'firebase/firestore';
import styles from './Dashboard.module.css';

// React Icons
import {
    FiClock, FiCheckCircle, FiAlertCircle, FiPackage, FiBox,
    FiTrendingUp, FiBarChart2, FiLink, FiSearch, FiX,
    FiRefreshCw, FiUsers, FiCalendar, FiClipboard, FiEye,
    FiAlertTriangle, FiInfo, FiDroplet, FiFileText, FiList,
    FiTarget, FiFlag, FiPlay, FiPause, FiCheck, FiPlus,
    FiDownload, FiPrinter, FiFilter, FiChevronDown, FiChevronUp,
    FiExternalLink, FiSettings, FiBell, FiMoreVertical
} from 'react-icons/fi';
import {
    FaFlask, FaMicroscope, FaVial, FaClipboardCheck,
    FaChartLine, FaBalanceScale, FaThermometerHalf,
    FaClock, FaExclamationTriangle, FaCheckDouble
} from 'react-icons/fa';
import {
    MdProductionQuantityLimits,
    MdScience, MdInventory, MdWarning, MdCheckCircle,
    MdCancel, MdPending, MdApproval, MdTimeline,
    MdShowChart, MdAnalytics
} from 'react-icons/md';

// Constants
const PRODUCTION_ORDERS_COLLECTION = 'production-orders';
const PRODUCTS_COLLECTION = 'products';
const RAW_MATERIALS_COLLECTION = 'raw-materials';
const CURRENT_STOCK_COLLECTION = 'current-stock';
const QUALITY_TESTS_COLLECTION = 'quality-tests';
const BATCH_GENEALOGY_COLLECTION = 'batch-genealogy';
const QUALITY_CRITERIA_COLLECTION = 'parameters/data/criterio-qualidade';
const DEVELOPMENT_PROJECTS_COLLECTION = 'development-projects';
const STOCK_ENTRIES_COLLECTION = 'stock-entries';
const STOCK_OUTFLOWS_COLLECTION = 'stock-outflows';

// Phase display names
const PHASE_DISPLAY_NAMES = {
    'phase-1': 'Pesagem',
    'phase-2': 'Início da Produção',
    'phase-3': 'Término da Produção',
    'phase-4': 'Início do CQ',
    'phase-5': 'CQ em Processo',
    'phase-6': 'Liberação do CQ',
    'phase-7': 'Conferência de Embalagem',
    'phase-8': 'Início do Envase',
    'phase-9': 'Término do Envase',
    'phase-10': 'Término da Rotulagem'
};

// Expanded phase descriptions for tooltips
const PHASE_DESCRIPTIONS = {
    'phase-1': 'Pesagem precisa de cada componente conforme formulação',
    'phase-2': 'Registro do início da produção com data de fabricação',
    'phase-3': 'Registro do término da produção',
    'phase-4': 'Início das análises de controle de qualidade',
    'phase-5': 'Registro dos resultados das análises em processo',
    'phase-6': 'Liberação do lote após aprovação do CQ',
    'phase-7': 'Verificação das embalagens utilizadas no envase',
    'phase-8': 'Início da operação de envase',
    'phase-9': 'Término da operação de envase',
    'phase-10': 'Término da rotulagem com quantidades conferidas'
};

// Status colors for quality tests
const QUALITY_STATUS_COLORS = {
    'pending': { bg: '#fff3cd', color: '#856404', label: 'Pendente' },
    'in-progress': { bg: '#cce5ff', color: '#004085', label: 'Em Andamento' },
    'approved': { bg: '#d4edda', color: '#155724', label: 'Aprovado' },
    'rejected': { bg: '#f8d7da', color: '#721c24', label: 'Rejeitado' },
    'retest': { bg: '#fff3cd', color: '#856404', label: 'Re-teste' }
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

// Quality Metrics Card
const QualityMetricCard = ({ title, value, subtitle, icon, color, trend, trendLabel }) => (
    <div className={styles.metricsCard}>
        <div className={styles.metricsHeader}>
            <div className={styles.metricsIcon} style={{ backgroundColor: color.bg, color: color.icon }}>
                {icon}
            </div>
            {trend !== undefined && (
                <div className={styles.metricsPercentage} style={{ color: trend > 0 ? 'var(--success-color)' : 'var(--danger-color)' }}>
                    {trend > 0 ? '+' : ''}{trend}%
                </div>
            )}
        </div>
        <div className={styles.metricsValue}>{value}</div>
        <div className={styles.metricsLabel}>{title}</div>
        <div className={styles.metricsSubtitle}>{subtitle}</div>
    </div>
);

// Phase Progress Bar
const PhaseProgressBar = ({ phases, currentPhase, completedPhases, onPhaseClick }) => {
    const totalPhases = phases.length;
    const completedCount = completedPhases.length;

    return (
        <div className={styles.phaseProgressContainer}>
            <div className={styles.phaseProgressHeader}>
                <span>Progresso das Fases</span>
                <span>{completedCount}/{totalPhases} concluídas</span>
            </div>
            <div className={styles.phaseProgressBar}>
                {phases.map((phase, index) => {
                    const isCompleted = completedPhases.includes(phase.id);
                    const isCurrent = currentPhase === phase.id;
                    const isPending = !isCompleted && !isCurrent;

                    let statusClass = styles.phasePending;
                    if (isCompleted) statusClass = styles.phaseCompleted;
                    if (isCurrent) statusClass = styles.phaseCurrent;

                    return (
                        <div
                            key={phase.id}
                            className={`${styles.phaseStep} ${statusClass}`}
                            onClick={() => onPhaseClick && onPhaseClick(phase.id)}
                            title={PHASE_DESCRIPTIONS[phase.id] || phase.name}
                            style={{ cursor: 'pointer' }}
                        >
                            <span className={styles.phaseNumber}>{index + 1}</span>
                            <span className={styles.phaseLabel}>{PHASE_DISPLAY_NAMES[phase.id] || phase.name}</span>
                            {isCompleted && <FiCheck size={12} className={styles.phaseCheck} />}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const LaboratorioDashboard = ({ user }) => {
    const { t } = useTranslation();

    // --- State ---
    const [isLoading, setIsLoading] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(null);
    const [traceabilityData, setTraceabilityData] = useState(null);
    const [traceabilityLoading, setTraceabilityLoading] = useState(false);
    const [bottleneckData, setBottleneckData] = useState([]);
    const [predictiveAlerts, setPredictiveAlerts] = useState([]);
    const [urgentTasks, setUrgentTasks] = useState([]);
    const [focusMode, setFocusMode] = useState(false);
    const [selectedBatch, setSelectedBatch] = useState('');
    const [pendingTests, setPendingTests] = useState(0);
    const [approvedTests, setApprovedTests] = useState(0);
    const [qualityAlerts, setQualityAlerts] = useState(0);
    const [expiringMaterials, setExpiringMaterials] = useState(0);
    const [activeOrders, setActiveOrders] = useState(0);
    const [qualityPendingOrders, setQualityPendingOrders] = useState(0);
    const [recentQualityResults, setRecentQualityResults] = useState([]);
    const [materialStockStatus, setMaterialStockStatus] = useState([]);
    const [lowStockMaterials, setLowStockMaterials] = useState([]); // NEW: for materials under 100kg
    const [waterMaterials, setWaterMaterials] = useState([]);
    const [qualityTestStats, setQualityTestStats] = useState({ pending: 0, approved: 0, rejected: 0 });
    const [phaseDistribution, setPhaseDistribution] = useState({});
    const [qualityCriteria, setQualityCriteria] = useState([]);
    const [developmentProjects, setDevelopmentProjects] = useState([]);
    const [activeProjects, setActiveProjects] = useState(0);
    const [qualityTrend, setQualityTrend] = useState({ approved: 0, rejected: 0, pending: 0 });
    const [showAlertsModal, setShowAlertsModal] = useState(false);
    const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
    const [showOrderDetail, setShowOrderDetail] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    // Refs for debouncing and cleanup
    const refreshTimeoutRef = useRef(null);
    const unsubscribeRefs = useRef([]);

    // --- Fetch Quality Criteria ---
    const fetchQualityCriteria = useCallback(async () => {
        try {
            const criteriaRef = collection(db, QUALITY_CRITERIA_COLLECTION);
            const criteriaSnapshot = await getDocs(criteriaRef);
            const criteria = criteriaSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setQualityCriteria(criteria);
            return criteria;
        } catch (error) {
            console.error("Error fetching quality criteria:", error);
            return [];
        }
    }, []);

    // --- Fetch Development Projects ---
    const fetchDevelopmentProjects = useCallback(async () => {
        try {
            const projectsRef = collection(db, DEVELOPMENT_PROJECTS_COLLECTION);
            const projectsSnapshot = await getDocs(projectsRef);
            const projects = projectsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            const active = projects.filter(p =>
                p.situation === 'Active' ||
                p.situation === 'Em Andamento' ||
                p.status === 'active'
            );
            setActiveProjects(active.length);
            setDevelopmentProjects(projects);
            return projects;
        } catch (error) {
            console.error("Error fetching development projects:", error);
            return [];
        }
    }, []);

    // --- IMPROVED: Fetch Batch Genealogy ---
    const fetchBatchGenealogy = useCallback(async (batchId) => {
        if (!batchId || batchId.length < 2) {
            setTraceabilityData({
                notFound: true,
                message: 'Digite um código de lote válido (mínimo 2 caracteres)'
            });
            return;
        }

        setTraceabilityLoading(true);
        try {
            const searchCode = batchId.toUpperCase().trim();
            const genealogy = {
                material: null,
                productionOrders: [],
                finishedProducts: [],
                stockEntries: [],
                stockOutflows: [],
                currentStock: [],
                upstreamMaterials: [],
                downstreamProducts: [],
                allRelatedLots: [],
                notFound: false,
                message: ''
            };

            // 1. Search in batch-genealogy collection first (most reliable)
            const genealogyRef = collection(db, BATCH_GENEALOGY_COLLECTION);
            const genealogyQuery = query(
                genealogyRef,
                where('batchId', '==', searchCode)
            );
            const genealogySnapshot = await getDocs(genealogyQuery);

            if (!genealogySnapshot.empty) {
                const data = genealogySnapshot.docs[0].data();
                genealogy.material = data.material || null;
                genealogy.productionOrders = data.productionOrders || [];
                genealogy.finishedProducts = data.finishedProducts || [];
                genealogy.stockEntries = data.stockEntries || [];
                genealogy.stockOutflows = data.stockOutflows || [];
                genealogy.currentStock = data.currentStock || [];
                genealogy.allRelatedLots = data.allRelatedLots || [];

                setTraceabilityData(genealogy);
                setTraceabilityLoading(false);
                return;
            }

            // 2. Search in stock entries by lot number
            const entriesRef = collection(db, STOCK_ENTRIES_COLLECTION);
            const entriesQuery = query(
                entriesRef,
                where('lotNumber', '==', searchCode)
            );
            const entriesSnapshot = await getDocs(entriesQuery);

            // 3. Search in current stock by lot number
            const stockRef = collection(db, CURRENT_STOCK_COLLECTION);
            const stockQuery = query(
                stockRef,
                where('lotNumber', '==', searchCode)
            );
            const stockSnapshot = await getDocs(stockQuery);

            // 4. Search raw materials by code
            const rmRef = collection(db, RAW_MATERIALS_COLLECTION);
            const rmQuery = query(
                rmRef,
                where('code', '==', searchCode)
            );
            const rmSnapshot = await getDocs(rmQuery);

            // 5. Search products by code
            const productsRef = collection(db, PRODUCTS_COLLECTION);
            const productsQuery = query(
                productsRef,
                where('code', '==', searchCode)
            );
            const productsSnapshot = await getDocs(productsQuery);

            // 6. Search production orders by OP code or containing the material
            const ordersRef = collection(db, PRODUCTION_ORDERS_COLLECTION);
            const ordersSnapshot = await getDocs(ordersRef);
            const allOrders = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Build genealogy from found data
            let foundMaterial = null;
            let foundEntries = [];
            let foundStock = [];
            let relatedOrders = [];
            let relatedProducts = [];
            let relatedLots = new Set();

            // Check if found in raw materials
            if (!rmSnapshot.empty) {
                const rmData = rmSnapshot.docs[0].data();
                foundMaterial = {
                    id: rmSnapshot.docs[0].id,
                    ...rmData,
                    isProduct: false,
                    type: 'raw_material'
                };
            }

            // Check if found in products
            if (!productsSnapshot.empty && !foundMaterial) {
                const prodData = productsSnapshot.docs[0].data();
                foundMaterial = {
                    id: productsSnapshot.docs[0].id,
                    ...prodData,
                    isProduct: true,
                    type: 'product'
                };
            }

            // If material found, find related stock entries and orders
            if (foundMaterial) {
                // Get all stock entries for this material
                const materialEntriesQuery = query(
                    entriesRef,
                    where('rawMaterialId', '==', foundMaterial.id)
                );
                const materialEntriesSnapshot = await getDocs(materialEntriesQuery);
                foundEntries = materialEntriesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                // Filter entries with the specific lot if searching by lot
                if (entriesSnapshot.size > 0) {
                    foundEntries = foundEntries.filter(e =>
                        e.lotNumber === searchCode
                    );
                    // Add lot to related lots
                    relatedLots.add(searchCode);
                }

                // Get current stock
                const materialStockQuery = query(
                    stockRef,
                    where('rawMaterialId', '==', foundMaterial.id)
                );
                const materialStockSnapshot = await getDocs(materialStockQuery);
                foundStock = materialStockSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                // Filter stock by lot if searching by lot
                if (stockSnapshot.size > 0) {
                    foundStock = foundStock.filter(s =>
                        s.lotNumber === searchCode
                    );
                }

                // Collect all lots from stock
                foundStock.forEach(s => {
                    if (s.lotNumber) relatedLots.add(s.lotNumber);
                });
                foundEntries.forEach(e => {
                    if (e.lotNumber) relatedLots.add(e.lotNumber);
                });

                // Find production orders using this material
                allOrders.forEach(order => {
                    // Check if material is in materialsRequired
                    const usesMaterial = order.materialsRequired?.some(m =>
                        m.id === foundMaterial.id ||
                        m.id === foundMaterial.code ||
                        m.rawMaterialId === foundMaterial.id
                    );

                    // Check if material is in items (as product)
                    const isProductInOrder = order.items?.some(item =>
                        item.productId === foundMaterial.id ||
                        item.productCode === foundMaterial.code
                    );

                    // Check if order's items have matching lot
                    const hasMatchingLot = order.items?.some(item =>
                        item.lote === searchCode ||
                        item.batchNumber === searchCode
                    );

                    if (usesMaterial || isProductInOrder || hasMatchingLot) {
                        const relation = hasMatchingLot ? 'produced_lot' :
                            usesMaterial ? 'input' : 'output';

                        relatedOrders.push({
                            id: order.id,
                            ...order,
                            relation: relation
                        });

                        // Collect lots from this order
                        if (order.items) {
                            order.items.forEach(item => {
                                if (item.lote) relatedLots.add(item.lote);
                                if (item.batchNumber) relatedLots.add(item.batchNumber);
                            });
                        }

                        // If this order produces products, add them to finished products
                        if (order.items) {
                            order.items.forEach(item => {
                                if (!relatedProducts.find(p => p.id === item.productId)) {
                                    relatedProducts.push({
                                        id: item.productId,
                                        code: item.productCode,
                                        name: item.productName,
                                        lote: item.lote,
                                        quantity: item.quantity,
                                        unit: item.mode === 'mass' ? 'Kg' : 'Un'
                                    });
                                    if (item.lote) relatedLots.add(item.lote);
                                }
                            });
                        }
                    }
                });

                // Try to trace downstream products (products made from this material)
                if (!foundMaterial.isProduct) {
                    // Find products that use this material in their formula
                    const allProducts = productsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    const downstreamProducts = allProducts.filter(p =>
                        p.rawMaterials?.some(rm =>
                            rm.id === foundMaterial.id ||
                            rm.code === foundMaterial.code
                        )
                    );

                    downstreamProducts.forEach(product => {
                        if (!relatedProducts.find(p => p.id === product.id)) {
                            relatedProducts.push({
                                id: product.id,
                                code: product.code,
                                name: product.commercialName || product.name,
                                isDownstream: true
                            });
                        }
                    });
                }

                // Try to trace upstream (materials used to make this product)
                if (foundMaterial.isProduct) {
                    const upstreamMaterials = [];
                    const upstreamLots = new Set();

                    allOrders.forEach(order => {
                        if (order.items?.some(item => item.productId === foundMaterial.id)) {
                            order.materialsRequired?.forEach(rm => {
                                if (!upstreamMaterials.find(m => m.id === rm.id)) {
                                    upstreamMaterials.push(rm);
                                }
                            });
                            // Get lots from materials used
                            if (order.materialsUsed) {
                                Object.values(order.materialsUsed).forEach(used => {
                                    if (used.lotNumber) upstreamLots.add(used.lotNumber);
                                });
                            }
                        }
                    });

                    genealogy.upstreamMaterials = upstreamMaterials;
                    upstreamLots.forEach(lot => relatedLots.add(lot));
                }

                // Build the genealogy data
                genealogy.material = foundMaterial;
                genealogy.stockEntries = foundEntries;
                genealogy.stockOutflows = []; // Could add outflows if needed
                genealogy.currentStock = foundStock;
                genealogy.productionOrders = relatedOrders;
                genealogy.finishedProducts = relatedProducts;
                genealogy.allRelatedLots = Array.from(relatedLots);

            } else {
                // If no material found, check if it's a lot from stock entries
                if (entriesSnapshot.size > 0) {
                    // Get the first entry to find the material
                    const firstEntry = entriesSnapshot.docs[0].data();
                    const materialId = firstEntry.rawMaterialId;

                    if (materialId) {
                        // Fetch the material
                        const materialDoc = await getDoc(doc(db, RAW_MATERIALS_COLLECTION, materialId));
                        if (materialDoc.exists()) {
                            foundMaterial = {
                                id: materialDoc.id,
                                ...materialDoc.data(),
                                isProduct: false,
                                type: 'raw_material'
                            };
                            genealogy.material = foundMaterial;

                            // Get all entries with this lot
                            const lotEntriesQuery = query(
                                entriesRef,
                                where('rawMaterialId', '==', materialId),
                                where('lotNumber', '==', searchCode)
                            );
                            const lotEntriesSnap = await getDocs(lotEntriesQuery);
                            genealogy.stockEntries = lotEntriesSnap.docs.map(doc => ({
                                id: doc.id,
                                ...doc.data()
                            }));

                            // Also get current stock with this lot
                            const lotStockQuery = query(
                                stockRef,
                                where('rawMaterialId', '==', materialId),
                                where('lotNumber', '==', searchCode)
                            );
                            const lotStockSnap = await getDocs(lotStockQuery);
                            genealogy.currentStock = lotStockSnap.docs.map(doc => ({
                                id: doc.id,
                                ...doc.data()
                            }));

                            // Find orders that used this lot
                            allOrders.forEach(order => {
                                if (order.materialsUsed) {
                                    const usesLot = Object.values(order.materialsUsed).some(
                                        used => used.lotNumber === searchCode
                                    );
                                    if (usesLot) {
                                        relatedOrders.push({
                                            id: order.id,
                                            ...order,
                                            relation: 'used_lot'
                                        });
                                    }
                                }
                            });
                            genealogy.productionOrders = relatedOrders;
                        }
                    }
                }

                // Also check if it's a lot from stock outflows
                if (genealogy.stockEntries.length === 0) {
                    const outflowsRef = collection(db, STOCK_OUTFLOWS_COLLECTION);
                    const outflowsQuery = query(
                        outflowsRef,
                        where('lotNumber', '==', searchCode)
                    );
                    const outflowsSnapshot = await getDocs(outflowsQuery);

                    if (!outflowsSnapshot.empty) {
                        const firstOutflow = outflowsSnapshot.docs[0].data();
                        const materialId = firstOutflow.rawMaterialId;

                        if (materialId) {
                            const materialDoc = await getDoc(doc(db, RAW_MATERIALS_COLLECTION, materialId));
                            if (materialDoc.exists()) {
                                foundMaterial = {
                                    id: materialDoc.id,
                                    ...materialDoc.data(),
                                    isProduct: false,
                                    type: 'raw_material'
                                };
                                genealogy.material = foundMaterial;
                                genealogy.stockOutflows = outflowsSnapshot.docs.map(doc => ({
                                    id: doc.id,
                                    ...doc.data()
                                }));
                            }
                        }
                    }
                }
            }

            // If still nothing found, mark as not found
            if (!genealogy.material && genealogy.stockEntries.length === 0 && genealogy.stockOutflows.length === 0) {
                genealogy.notFound = true;
                genealogy.message = `Nenhum registro encontrado para o lote/código: ${batchId}`;
            } else {
                // Add a summary message
                const totalEntries = genealogy.stockEntries.length + genealogy.stockOutflows.length;
                const totalOrders = genealogy.productionOrders.length;
                const totalProducts = genealogy.finishedProducts.length;

                genealogy.message = `Encontrados: ${totalEntries} registros de estoque, ${totalOrders} ordens de produção, ${totalProducts} produtos relacionados`;

                // If we found a lot but no material, try to find it through entries
                if (!genealogy.material && genealogy.stockEntries.length > 0) {
                    const firstEntry = genealogy.stockEntries[0];
                    if (firstEntry.rawMaterialId) {
                        try {
                            const matDoc = await getDoc(doc(db, RAW_MATERIALS_COLLECTION, firstEntry.rawMaterialId));
                            if (matDoc.exists()) {
                                genealogy.material = {
                                    id: matDoc.id,
                                    ...matDoc.data(),
                                    isProduct: false,
                                    type: 'raw_material'
                                };
                            }
                        } catch (e) {
                            // Ignore
                        }
                    }
                }
            }

            setTraceabilityData(genealogy);

        } catch (error) {
            console.error("Error fetching genealogy:", error);
            setTraceabilityData({
                notFound: true,
                message: 'Erro ao buscar rastreabilidade. Tente novamente.'
            });
        } finally {
            setTraceabilityLoading(false);
        }
    }, []);

    // --- Fetch Dashboard Data ---
    const fetchDashboardData = useCallback(async () => {
        setIsLoading(true);
        try {
            // Clean up old listeners
            unsubscribeRefs.current.forEach(unsub => {
                try { unsub(); } catch (e) { /* ignore */ }
            });
            unsubscribeRefs.current = [];

            // Fetch quality criteria
            const criteria = await fetchQualityCriteria();

            // Fetch development projects
            const projects = await fetchDevelopmentProjects();

            // Fetch production orders
            const ordersRef = collection(db, PRODUCTION_ORDERS_COLLECTION);
            const ordersSnapshot = await getDocs(ordersRef);
            const allOrders = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Active orders count
            const active = allOrders.filter(o =>
                o.status === 'active' ||
                o.status === 'awaiting-start' ||
                o.status === 'awaiting-mp'
            );
            setActiveOrders(active.length);

            // Phase distribution
            const phaseDist = {};
            active.forEach(order => {
                if (order.currentPhase) {
                    phaseDist[order.currentPhase] = (phaseDist[order.currentPhase] || 0) + 1;
                }
            });
            setPhaseDistribution(phaseDist);

            // Orders awaiting quality control (phase 4, 5, 6)
            const qualityPending = allOrders.filter(o =>
                o.status === 'active' &&
                (o.currentPhase === 'phase-4' ||
                    o.currentPhase === 'phase-5' ||
                    o.currentPhase === 'phase-6')
            );
            setQualityPendingOrders(qualityPending.length);

            // Fetch products
            const productsRef = collection(db, PRODUCTS_COLLECTION);
            const productsSnapshot = await getDocs(productsRef);
            const products = productsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Fetch raw materials with stock
            const rmRef = collection(db, RAW_MATERIALS_COLLECTION);
            const rmSnapshot = await getDocs(rmRef);
            const materials = rmSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Fetch current stock
            const stockRef = collection(db, CURRENT_STOCK_COLLECTION);
            const stockSnapshot = await getDocs(stockRef);
            const stockItems = stockSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Fetch quality tests
            const testsRef = collection(db, QUALITY_TESTS_COLLECTION);
            const testsSnapshot = await getDocs(testsRef);
            const qualityTests = testsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Calculate quality test statistics with trend
            const pending = qualityTests.filter(test =>
                test.status === 'pending' || test.status === 'in-progress'
            ).length;
            const approved = qualityTests.filter(test => test.status === 'approved').length;
            const rejected = qualityTests.filter(test => test.status === 'rejected').length;

            setPendingTests(pending);
            setApprovedTests(approved);
            setQualityTestStats({ pending, approved, rejected });

            // Calculate quality trend (based on last 30 days)
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

            const recentTests = qualityTests.filter(test => {
                if (!test.createdAt) return false;
                try {
                    const date = test.createdAt.toDate ? test.createdAt.toDate() : new Date(test.createdAt);
                    return date >= thirtyDaysAgo;
                } catch { return false; }
            });

            const recentApproved = recentTests.filter(t => t.status === 'approved').length;
            const recentRejected = recentTests.filter(t => t.status === 'rejected').length;
            const totalRecent = recentTests.length;

            setQualityTrend({
                approved: totalRecent > 0 ? (recentApproved / totalRecent) * 100 : 0,
                rejected: totalRecent > 0 ? (recentRejected / totalRecent) * 100 : 0,
                pending: totalRecent > 0 ? ((totalRecent - recentApproved - recentRejected) / totalRecent) * 100 : 0
            });

            // Calculate expiring materials (30 days from now)
            const thirtyDaysFromNow = new Date();
            thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);

            const expiring = materials.filter(m => {
                if (!m.expirationDate) return false;
                try {
                    const expDate = m.expirationDate.toDate ? m.expirationDate.toDate() : new Date(m.expirationDate);
                    return expDate < thirtyDaysFromNow && expDate > new Date();
                } catch { return false; }
            });
            setExpiringMaterials(expiring.length);

            // Find water materials
            const waterCodes = ['MP001', 'AGUA001', 'WATER001'];
            const waterMats = materials.filter(m =>
                waterCodes.includes(m.code?.toUpperCase()) ||
                waterCodes.includes(m.id?.toUpperCase())
            );
            setWaterMaterials(waterMats);

            // Generate predictive alerts from real quality test data
            const alerts = [];
            const qualityResults = [];

            // Check products with quality criteria failures in history
            for (const product of products) {
                if (product.qualityCriteria && product.qualityCriteria.length > 0) {
                    const productOrders = allOrders.filter(o =>
                        o.items?.some(item => item.productId === product.id)
                    );

                    if (productOrders.length >= 2) {
                        let totalBatches = 0;
                        const criteriaFailures = {};

                        productOrders.forEach(order => {
                            totalBatches++;
                            product.qualityCriteria.forEach(criteria => {
                                if (order.qualityResults?.[criteria.id]?.status === 'rejected') {
                                    criteriaFailures[criteria.id] = (criteriaFailures[criteria.id] || 0) + 1;
                                }
                            });
                        });

                        for (const criteria of product.qualityCriteria) {
                            const failureRate = ((criteriaFailures[criteria.id] || 0) / totalBatches) * 100;
                            if (failureRate > 15) {
                                alerts.push({
                                    type: 'critical',
                                    productName: product.commercialName || product.name,
                                    criteria: criteria.analysis,
                                    failureRate: failureRate.toFixed(1),
                                    message: `${product.commercialName || product.name}: ${failureRate.toFixed(1)}% de falha em "${criteria.analysis}"`,
                                    severity: 'high'
                                });
                            } else if (failureRate > 5) {
                                alerts.push({
                                    type: 'warning',
                                    productName: product.commercialName || product.name,
                                    criteria: criteria.analysis,
                                    failureRate: failureRate.toFixed(1),
                                    message: `${product.commercialName || product.name}: ${failureRate.toFixed(1)}% de ajuste em "${criteria.analysis}"`,
                                    severity: 'medium'
                                });
                            }
                        }

                        // Recent quality results from actual data
                        productOrders.slice(0, 5).forEach(order => {
                            if (order.qualityResults) {
                                Object.entries(order.qualityResults).forEach(([key, result]) => {
                                    qualityResults.push({
                                        productName: product.commercialName || product.name,
                                        opCode: order.opCode,
                                        criteria: key,
                                        status: result.status,
                                        value: result.value,
                                        date: order.completedAt || order.updatedAt,
                                        orderId: order.id
                                    });
                                });
                            }
                        });
                    }
                }
            }

            // Also add quality tests from the quality-tests collection
            qualityTests.forEach(test => {
                const product = products.find(p => p.id === test.productId);
                qualityResults.push({
                    productName: product?.commercialName || product?.name || test.productName || 'Produto',
                    opCode: test.opCode || 'N/A',
                    criteria: test.criteria || 'Análise',
                    status: test.status || 'pending',
                    value: test.value || '—',
                    date: test.createdAt || test.updatedAt,
                    orderId: test.orderId || null
                });
            });

            // Filter to only show recent results (last 30 days)
            const recentQualityResultsFiltered = qualityResults.filter(result => {
                if (!result.date) return true;
                try {
                    const date = result.date.toDate ? result.date.toDate() : new Date(result.date);
                    return date >= thirtyDaysAgo;
                } catch { return true; }
            });

            setPredictiveAlerts(alerts.slice(0, 8));
            setQualityAlerts(alerts.filter(a => a.type === 'critical').length);

            // Set recent quality results (latest 15)
            const sortedResults = qualityResults.sort((a, b) => {
                const dateA = a.date ? (a.date.toDate ? a.date.toDate() : new Date(a.date)) : new Date(0);
                const dateB = b.date ? (b.date.toDate ? b.date.toDate() : new Date(b.date)) : new Date(0);
                return dateB - dateA;
            });
            setRecentQualityResults(sortedResults.slice(0, 15));

            // --- UPDATED: Material stock status - filter to show only materials under 100kg ---
            const waterCodesList = ['MP001', 'AGUA001', 'WATER001'];

            // Calculate stock for all materials first
            const allStockStatus = materials.map(m => {
                const isWater = waterCodesList.includes(m.code?.toUpperCase()) ||
                    waterCodesList.includes(m.id?.toUpperCase());
                const totalQty = stockItems
                    .filter(s => s.rawMaterialId === m.id)
                    .reduce((sum, s) => sum + (s.quantity || 0), 0);

                return {
                    id: m.id,
                    code: m.code,
                    name: m.commercialName || m.inciName || m.name,
                    quantity: totalQty || 0,
                    isWater,
                    unit: isWater ? 'L' : 'Kg',
                    locationCount: stockItems.filter(s => s.rawMaterialId === m.id).length,
                    hasStock: totalQty > 0 || isWater,
                    isLowStock: !isWater && totalQty > 0 && totalQty <= 100 // Under 100kg
                };
            });

            // Filter to show only materials with low stock (under 100kg and > 0)
            const lowStockItems = allStockStatus
                .filter(m => m.isLowStock)
                .sort((a, b) => a.quantity - b.quantity); // Sort by lowest quantity first

            setLowStockMaterials(lowStockItems);

            // Also keep the full list for other uses if needed
            setMaterialStockStatus(allStockStatus.filter(m => m.hasStock).slice(0, 12));

            // Bottleneck analysis from real phase metrics
            const phaseTimes = {};
            allOrders.forEach(order => {
                const metrics = order.phaseMetrics || {};
                Object.entries(metrics).forEach(([phaseId, metric]) => {
                    if (metric.dwellTimeMinutes) {
                        if (!phaseTimes[phaseId]) {
                            phaseTimes[phaseId] = {
                                total: 0,
                                count: 0,
                                name: PHASE_DISPLAY_NAMES[phaseId] || phaseId,
                                completedAt: metric.completedAt
                            };
                        }
                        phaseTimes[phaseId].total += metric.dwellTimeMinutes;
                        phaseTimes[phaseId].count++;
                    }
                });
            });

            const bottlenecks = Object.entries(phaseTimes).map(([id, data]) => ({
                id,
                name: data.name,
                avgDwellTimeMinutes: data.total / data.count,
                sampleCount: data.count,
                maxDwellTime: data.total,
                completedAt: data.completedAt
            })).sort((a, b) => b.avgDwellTimeMinutes - a.avgDwellTimeMinutes);

            setBottleneckData(bottlenecks.slice(0, 10));

            // Urgent tasks from real data
            const tasks = [];

            // Expiring materials - urgent (7 days or less)
            const urgentExpiring = materials.filter(m => {
                if (!m.expirationDate) return false;
                try {
                    const expDate = m.expirationDate.toDate ? m.expirationDate.toDate() : new Date(m.expirationDate);
                    const daysLeft = Math.ceil((expDate - new Date()) / (1000 * 60 * 60 * 24));
                    return daysLeft <= 7 && daysLeft > 0;
                } catch { return false; }
            });

            urgentExpiring.slice(0, 3).forEach(m => {
                const daysLeft = Math.ceil(((m.expirationDate.toDate ? m.expirationDate.toDate() : new Date(m.expirationDate)) - new Date()) / (1000 * 60 * 60 * 24));
                tasks.push({
                    id: `exp-${m.id}`,
                    type: 'expiring_material',
                    title: `MP vencendo: ${m.commercialName || m.inciName || m.code}`,
                    priority: 'critical',
                    daysLeft,
                    urgency: daysLeft <= 3 ? 'critical' : 'high'
                });
            });

            // Quality pending orders with priority
            qualityPending.slice(0, 3).forEach(order => {
                // Check how long it's been in quality phase
                let dwellTime = 0;
                const phaseProgress = order.phaseProgress || {};
                const phaseData = phaseProgress[order.currentPhase] || {};
                if (phaseData.startedAt) {
                    const startDate = new Date(phaseData.startedAt);
                    dwellTime = (new Date() - startDate) / (1000 * 60 * 60); // hours
                }

                tasks.push({
                    id: `qc-${order.id}`,
                    type: 'qc_pending',
                    title: `OP ${order.opCode} - Aguardando CQ`,
                    priority: dwellTime > 4 ? 'critical' : 'medium',
                    orderCode: order.opCode,
                    dwellTime: Math.round(dwellTime * 10) / 10,
                    urgency: dwellTime > 4 ? 'critical' : 'medium'
                });
            });

            // Quality tests pending
            const pendingTestsList = qualityTests.filter(test =>
                test.status === 'pending' || test.status === 'in-progress'
            );
            pendingTestsList.slice(0, 3).forEach(test => {
                const product = products.find(p => p.id === test.productId);
                const age = test.createdAt ? Math.round((new Date() - (test.createdAt.toDate ? test.createdAt.toDate() : new Date(test.createdAt))) / (1000 * 60 * 60 * 24)) : 0;
                tasks.push({
                    id: `test-${test.id}`,
                    type: 'test_pending',
                    title: `Teste pendente: ${product?.commercialName || product?.name || test.productName || 'Produto'}`,
                    priority: age > 2 ? 'high' : 'medium',
                    age,
                    urgency: age > 2 ? 'high' : 'medium'
                });
            });

            // Sort tasks by priority (critical > high > medium)
            const priorityOrder = { critical: 0, high: 1, medium: 2 };
            tasks.sort((a, b) => (priorityOrder[a.priority] || 3) - (priorityOrder[b.priority] || 3));

            setUrgentTasks(tasks.slice(0, 8));

            // Update timestamp
            setLastUpdate(new Date());

        } catch (error) {
            console.error("Error fetching dashboard data:", error);
        } finally {
            setIsLoading(false);
        }
    }, [fetchQualityCriteria, fetchDevelopmentProjects]);

    // --- Set up real-time listeners ---
    useEffect(() => {
        fetchDashboardData();

        // Set up real-time listener for production orders
        const ordersUnsubscribe = onSnapshot(
            collection(db, PRODUCTION_ORDERS_COLLECTION),
            () => {
                if (refreshTimeoutRef.current) {
                    clearTimeout(refreshTimeoutRef.current);
                }
                refreshTimeoutRef.current = setTimeout(() => {
                    fetchDashboardData();
                }, 1000);
            },
            (error) => {
                console.error("Orders listener error:", error);
            }
        );
        unsubscribeRefs.current.push(ordersUnsubscribe);

        // Set up real-time listener for quality tests
        const testsUnsubscribe = onSnapshot(
            collection(db, QUALITY_TESTS_COLLECTION),
            () => {
                if (refreshTimeoutRef.current) {
                    clearTimeout(refreshTimeoutRef.current);
                }
                refreshTimeoutRef.current = setTimeout(() => {
                    fetchDashboardData();
                }, 1000);
            },
            (error) => {
                console.error("Quality tests listener error:", error);
            }
        );
        unsubscribeRefs.current.push(testsUnsubscribe);

        // Set up real-time listener for stock
        const stockUnsubscribe = onSnapshot(
            collection(db, CURRENT_STOCK_COLLECTION),
            () => {
                if (refreshTimeoutRef.current) {
                    clearTimeout(refreshTimeoutRef.current);
                }
                refreshTimeoutRef.current = setTimeout(() => {
                    fetchDashboardData();
                }, 2000);
            },
            (error) => {
                console.error("Stock listener error:", error);
            }
        );
        unsubscribeRefs.current.push(stockUnsubscribe);

        // Set up real-time listener for stock entries (for traceability)
        const entriesUnsubscribe = onSnapshot(
            collection(db, STOCK_ENTRIES_COLLECTION),
            () => {
                // If we have a selected batch, refresh its data
                if (selectedBatch && selectedBatch.trim().length >= 2) {
                    if (refreshTimeoutRef.current) {
                        clearTimeout(refreshTimeoutRef.current);
                    }
                    refreshTimeoutRef.current = setTimeout(() => {
                        fetchBatchGenealogy(selectedBatch);
                    }, 1000);
                }
            },
            (error) => {
                console.error("Stock entries listener error:", error);
            }
        );
        unsubscribeRefs.current.push(entriesUnsubscribe);

        // Set up interval for periodic refresh (every 60 seconds)
        const intervalId = setInterval(() => {
            fetchDashboardData();
        }, 60000);

        return () => {
            unsubscribeRefs.current.forEach(unsub => {
                try { unsub(); } catch (e) { /* ignore */ }
            });
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
            clearInterval(intervalId);
        };
    }, [fetchDashboardData, fetchBatchGenealogy, selectedBatch]);

    // --- Handle search for traceability ---
    const handleTraceabilitySearch = (e) => {
        if (e) e.preventDefault();
        if (selectedBatch.trim()) {
            fetchBatchGenealogy(selectedBatch.trim());
        }
    };

    // --- Handle view order details ---
    const handleViewOrderDetails = (orderId) => {
        const order = recentQualityResults.find(r => r.orderId === orderId);
        if (order) {
            setSelectedOrderDetails(order);
            setShowOrderDetail(true);
        }
    };

    // --- Handle refresh ---
    const handleRefresh = () => {
        setRefreshKey(prev => prev + 1);
        fetchDashboardData();
    };

    // --- Format time ago ---
    const timeAgo = (date) => {
        if (!date) return 'N/A';
        try {
            const d = date.toDate ? date.toDate() : new Date(date);
            const diff = Math.floor((new Date() - d) / 1000);
            if (diff < 60) return `${diff}s atrás`;
            if (diff < 3600) return `${Math.floor(diff / 60)}min atrás`;
            if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
            return `${Math.floor(diff / 86400)}d atrás`;
        } catch {
            return 'N/A';
        }
    };

    // ============================================================================
    // UPDATED: Traceability Graph Component
    // ============================================================================
    const TraceabilityGraph = () => {
        if (traceabilityLoading) {
            return (
                <div className={styles.emptyState}>
                    <div className={styles.spinner}></div>
                    <p>Buscando rastreabilidade...</p>
                </div>
            );
        }

        if (!traceabilityData) {
            return (
                <div className={styles.emptyState}>
                    <FiLink size={32} />
                    <p>Busque um lote para visualizar a rastreabilidade</p>
                    <span>Digite o código da matéria-prima, produto acabado ou número de lote</span>
                </div>
            );
        }

        if (traceabilityData.notFound) {
            return (
                <div className={styles.emptyState}>
                    <FiAlertCircle size={32} style={{ color: 'var(--warning-color)' }} />
                    <p>{traceabilityData.message || 'Lote não encontrado'}</p>
                    <span>Verifique o código digitado e tente novamente</span>
                </div>
            );
        }

        const hasData = traceabilityData.material ||
            traceabilityData.stockEntries?.length > 0 ||
            traceabilityData.productionOrders?.length > 0 ||
            traceabilityData.stockOutflows?.length > 0;

        if (!hasData) {
            return (
                <div className={styles.emptyState}>
                    <FiInfo size={32} />
                    <p>Nenhuma informação de rastreabilidade encontrada</p>
                    <span>Tente buscar por um código diferente</span>
                </div>
            );
        }

        return (
            <div className={styles.traceabilityGraph}>
                {/* Summary Message */}
                {traceabilityData.message && (
                    <div style={{
                        padding: '8px 12px',
                        backgroundColor: 'var(--bg-secondary)',
                        borderRadius: '4px',
                        marginBottom: '16px',
                        fontSize: '0.85rem',
                        color: 'var(--text-secondary)'
                    }}>
                        {traceabilityData.message}
                    </div>
                )}

                {/* Material/Lot Information */}
                {traceabilityData.material && (
                    <div className={styles.traceRoot}>
                        <div className={styles.traceNode} style={{
                            padding: '12px 16px',
                            backgroundColor: 'var(--bg-secondary)',
                            borderRadius: '8px',
                            borderLeft: `3px solid ${traceabilityData.material.isProduct ? 'var(--success-color)' : 'var(--primary-color)'}`,
                            marginBottom: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            flexWrap: 'wrap'
                        }}>
                            <span style={{ fontSize: '24px' }}>
                                {traceabilityData.material.isProduct ? '📦' : '🧪'}
                            </span>
                            <div style={{ flex: 1 }}>
                                <strong>{traceabilityData.material.code}</strong>
                                <span style={{ marginLeft: '8px', color: 'var(--text-secondary)' }}>
                                    {traceabilityData.material.commercialName ||
                                        traceabilityData.material.inciName ||
                                        traceabilityData.material.name}
                                </span>
                                {traceabilityData.material.isProduct && (
                                    <span style={{
                                        marginLeft: '8px',
                                        fontSize: '0.7rem',
                                        padding: '2px 8px',
                                        backgroundColor: 'var(--success-light)',
                                        borderRadius: '12px',
                                        color: 'var(--success-color)'
                                    }}>
                                        Produto Acabado
                                    </span>
                                )}
                            </div>
                            {traceabilityData.allRelatedLots && traceabilityData.allRelatedLots.length > 0 && (
                                <span style={{
                                    fontSize: '0.7rem',
                                    padding: '2px 8px',
                                    backgroundColor: 'var(--bg-tertiary)',
                                    borderRadius: '12px',
                                    color: 'var(--text-muted)'
                                }}>
                                    {traceabilityData.allRelatedLots.length} lotes relacionados
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* Current Stock */}
                {traceabilityData.currentStock && traceabilityData.currentStock.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                        <div className={styles.traceBranchLabel} style={{
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            marginBottom: '6px',
                            color: 'var(--text-secondary)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <FiBox size={16} />
                            Estoque Atual ({traceabilityData.currentStock.length} registros)
                        </div>
                        {traceabilityData.currentStock.slice(0, 5).map((stock, idx) => (
                            <div key={stock.id || idx} style={{
                                padding: '6px 12px',
                                backgroundColor: 'var(--bg-secondary)',
                                borderRadius: '4px',
                                marginBottom: '4px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                fontSize: '0.85rem'
                            }}>
                                <span>
                                    {stock.locationId || 'Localização'}
                                    {stock.lotNumber && ` · Lote: ${stock.lotNumber}`}
                                </span>
                                <span style={{ fontWeight: 600 }}>
                                    {stock.quantity?.toFixed(3) || '0'} {stock.unit || 'un'}
                                </span>
                            </div>
                        ))}
                        {traceabilityData.currentStock.length > 5 && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: '4px' }}>
                                + {traceabilityData.currentStock.length - 5} mais registros
                            </div>
                        )}
                    </div>
                )}

                {/* Stock Entries */}
                {traceabilityData.stockEntries && traceabilityData.stockEntries.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                        <div className={styles.traceBranchLabel} style={{
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            marginBottom: '6px',
                            color: 'var(--text-secondary)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <FiPackage size={16} />
                            Entradas de Estoque ({traceabilityData.stockEntries.length})
                        </div>
                        {traceabilityData.stockEntries.slice(0, 5).map((entry, idx) => (
                            <div key={entry.id || idx} style={{
                                padding: '6px 12px',
                                backgroundColor: 'var(--bg-secondary)',
                                borderRadius: '4px',
                                marginBottom: '4px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                fontSize: '0.85rem',
                                borderLeft: '2px solid var(--success-color)'
                            }}>
                                <span>
                                    Lote: {entry.lotNumber || 'N/A'}
                                    {entry.locationId && ` · ${entry.locationId}`}
                                    {entry.expirationDate && ` · Val: ${formatDate(entry.expirationDate)}`}
                                </span>
                                <span style={{ fontWeight: 600, color: 'var(--success-color)' }}>
                                    +{entry.quantity?.toFixed(3) || '0'} {entry.unit || 'un'}
                                </span>
                            </div>
                        ))}
                        {traceabilityData.stockEntries.length > 5 && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: '4px' }}>
                                + {traceabilityData.stockEntries.length - 5} mais entradas
                            </div>
                        )}
                    </div>
                )}

                {/* Stock Outflows */}
                {traceabilityData.stockOutflows && traceabilityData.stockOutflows.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                        <div className={styles.traceBranchLabel} style={{
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            marginBottom: '6px',
                            color: 'var(--text-secondary)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <FiTrendingUp size={16} style={{ transform: 'rotate(180deg)' }} />
                            Saídas de Estoque ({traceabilityData.stockOutflows.length})
                        </div>
                        {traceabilityData.stockOutflows.slice(0, 5).map((outflow, idx) => (
                            <div key={outflow.id || idx} style={{
                                padding: '6px 12px',
                                backgroundColor: 'var(--bg-secondary)',
                                borderRadius: '4px',
                                marginBottom: '4px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                fontSize: '0.85rem',
                                borderLeft: '2px solid var(--danger-color)'
                            }}>
                                <span>
                                    Lote: {outflow.lotNumber || 'N/A'}
                                    {outflow.destination && ` → ${outflow.destination}`}
                                </span>
                                <span style={{ fontWeight: 600, color: 'var(--danger-color)' }}>
                                    -{outflow.quantity?.toFixed(3) || '0'} {outflow.unit || 'un'}
                                </span>
                            </div>
                        ))}
                        {traceabilityData.stockOutflows.length > 5 && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: '4px' }}>
                                + {traceabilityData.stockOutflows.length - 5} mais saídas
                            </div>
                        )}
                    </div>
                )}

                {/* Production Orders */}
                {traceabilityData.productionOrders && traceabilityData.productionOrders.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                        <div className={styles.traceBranchLabel} style={{
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            marginBottom: '6px',
                            color: 'var(--text-secondary)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <MdProductionQuantityLimits size={16} />
                            Ordens de Produção ({traceabilityData.productionOrders.length})
                        </div>
                        {traceabilityData.productionOrders.slice(0, 10).map((order) => {
                            const statusColors = {
                                'active': '#27ae60',
                                'awaiting-start': '#3498db',
                                'awaiting-mp': '#f39c12',
                                'completed': '#2ecc71',
                                'pending': '#95a5a6',
                                'cancelled': '#e74c3c'
                            };
                            const relationLabels = {
                                'input': '📥 Insumo',
                                'output': '📤 Produto',
                                'produced_lot': '🏷️ Lote Produzido',
                                'used_lot': '🔗 Lote Utilizado'
                            };

                            return (
                                <div key={order.id} style={{
                                    padding: '8px 12px',
                                    backgroundColor: 'var(--bg-secondary)',
                                    borderRadius: '4px',
                                    marginBottom: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    fontSize: '0.85rem',
                                    flexWrap: 'wrap'
                                }}>
                                    <span>📄</span>
                                    <div style={{ flex: 1, minWidth: '120px' }}>
                                        <strong>{order.opCode}</strong>
                                        <span style={{ marginLeft: '8px', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                                            {order.items?.[0]?.productName || 'N/A'}
                                        </span>
                                    </div>
                                    <span style={{
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                        fontSize: '0.65rem',
                                        backgroundColor: statusColors[order.status] || '#95a5a6',
                                        color: 'white'
                                    }}>
                                        {order.status || 'N/A'}
                                    </span>
                                    {order.currentPhase && (
                                        <span style={{
                                            fontSize: '0.6rem',
                                            color: 'var(--text-muted)',
                                            padding: '2px 6px',
                                            backgroundColor: 'var(--bg-tertiary)',
                                            borderRadius: '4px'
                                        }}>
                                            {PHASE_DISPLAY_NAMES[order.currentPhase] || order.currentPhase}
                                        </span>
                                    )}
                                    {order.relation && (
                                        <span style={{
                                            fontSize: '0.65rem',
                                            color: 'var(--text-secondary)',
                                            padding: '2px 6px',
                                            backgroundColor: 'var(--bg-tertiary)',
                                            borderRadius: '4px'
                                        }}>
                                            {relationLabels[order.relation] || order.relation}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                        {traceabilityData.productionOrders.length > 10 && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: '4px' }}>
                                + {traceabilityData.productionOrders.length - 10} mais ordens
                            </div>
                        )}
                    </div>
                )}

                {/* Finished Products */}
                {traceabilityData.finishedProducts && traceabilityData.finishedProducts.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                        <div className={styles.traceBranchLabel} style={{
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            marginBottom: '6px',
                            color: 'var(--text-secondary)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <FiCheckCircle size={16} style={{ color: 'var(--success-color)' }} />
                            Produtos Acabados ({traceabilityData.finishedProducts.length})
                        </div>
                        {traceabilityData.finishedProducts.slice(0, 10).map((product, idx) => (
                            <div key={product.id || idx} style={{
                                padding: '6px 12px',
                                backgroundColor: 'var(--bg-secondary)',
                                borderRadius: '4px',
                                marginBottom: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                fontSize: '0.85rem',
                                flexWrap: 'wrap'
                            }}>
                                <span>🏷️</span>
                                <div style={{ flex: 1 }}>
                                    <strong>{product.code}</strong>
                                    <span style={{ marginLeft: '8px', color: 'var(--text-secondary)' }}>
                                        {product.name}
                                    </span>
                                </div>
                                {product.lote && (
                                    <span style={{
                                        fontSize: '0.7rem',
                                        padding: '2px 8px',
                                        backgroundColor: 'var(--primary-light)',
                                        borderRadius: '12px',
                                        color: 'var(--primary-color)'
                                    }}>
                                        Lote: {product.lote}
                                    </span>
                                )}
                                {product.quantity && (
                                    <span style={{
                                        fontSize: '0.7rem',
                                        color: 'var(--text-muted)'
                                    }}>
                                        {product.quantity} {product.unit || ''}
                                    </span>
                                )}
                                {product.isDownstream && (
                                    <span style={{
                                        fontSize: '0.6rem',
                                        padding: '2px 6px',
                                        backgroundColor: 'var(--info-light)',
                                        borderRadius: '4px',
                                        color: 'var(--info-color)'
                                    }}>
                                        Derivado
                                    </span>
                                )}
                            </div>
                        ))}
                        {traceabilityData.finishedProducts.length > 10 && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: '4px' }}>
                                + {traceabilityData.finishedProducts.length - 10} mais produtos
                            </div>
                        )}
                    </div>
                )}

                {/* Upstream Materials */}
                {traceabilityData.upstreamMaterials && traceabilityData.upstreamMaterials.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                        <div className={styles.traceBranchLabel} style={{
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            marginBottom: '6px',
                            color: 'var(--text-secondary)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <FiTrendingUp size={16} />
                            Matérias-Primas Utilizadas ({traceabilityData.upstreamMaterials.length})
                        </div>
                        {traceabilityData.upstreamMaterials.slice(0, 5).map((mat, idx) => (
                            <div key={mat.id || idx} style={{
                                padding: '6px 12px',
                                backgroundColor: 'var(--bg-secondary)',
                                borderRadius: '4px',
                                marginBottom: '4px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                fontSize: '0.85rem'
                            }}>
                                <span>
                                    {mat.code} - {mat.name || mat.description}
                                </span>
                                {mat.quantity && (
                                    <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>
                                        {mat.quantity} {mat.unit || ''}
                                    </span>
                                )}
                            </div>
                        ))}
                        {traceabilityData.upstreamMaterials.length > 5 && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: '4px' }}>
                                + {traceabilityData.upstreamMaterials.length - 5} mais materiais
                            </div>
                        )}
                    </div>
                )}

                {/* Related Lots */}
                {traceabilityData.allRelatedLots && traceabilityData.allRelatedLots.length > 1 && (
                    <div style={{ marginTop: '12px', padding: '8px 12px', backgroundColor: 'var(--bg-tertiary)', borderRadius: '4px' }}>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                            Lotes relacionados: {traceabilityData.allRelatedLots.join(', ')}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Helper to format date
    const formatDate = (dateValue) => {
        if (!dateValue) return '—';
        try {
            let date;
            if (dateValue.toDate) {
                date = dateValue.toDate();
            } else if (typeof dateValue === 'string') {
                date = new Date(dateValue);
            } else if (dateValue instanceof Date) {
                date = dateValue;
            } else {
                date = new Date(dateValue);
            }
            if (isNaN(date.getTime())) return 'Inválida';
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        } catch {
            return 'Inválida';
        }
    };

    // ============================================================================
    // Focus Mode Component
    // ============================================================================
    const FocusMode = () => {
        if (!focusMode) return null;

        return (
            <div className={styles.modalOverlay} onClick={() => setFocusMode(false)}>
                <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.modalHeader}>
                        <h3><FiTarget size={20} /> Modo Foco - Próximas Ações</h3>
                        <button onClick={() => setFocusMode(false)} className={styles.modalCloseBtn}>
                            <FiX size={20} />
                        </button>
                    </div>
                    <div className={styles.modalBody}>
                        {urgentTasks.length === 0 ? (
                            <div className={styles.emptyState}>
                                <p>🎉 Nenhuma tarefa urgente no momento.</p>
                                <span>Todas as análises estão em dia.</span>
                            </div>
                        ) : (
                            <div className={styles.servicesList}>
                                {urgentTasks.map(task => {
                                    const priorityLabel = task.priority === 'critical' ? '⚠️ Crítico' :
                                        task.priority === 'high' ? '🔴 Urgente' : '🟡 Normal';
                                    return (
                                        <div key={task.id} className={styles.serviceItem}>
                                            <span className={styles.serviceIcon}>
                                                {task.type === 'expiring_material' ? '⏰' :
                                                    task.type === 'test_pending' ? '🔬' : '📋'}
                                            </span>
                                            <span className={styles.serviceLabel}>{task.title}</span>
                                            <span className={styles.serviceStatus}>
                                                {task.daysLeft !== undefined && (
                                                    <span style={{
                                                        color: task.daysLeft < 3 ? 'var(--danger-color)' :
                                                            task.daysLeft < 7 ? 'var(--warning-color)' : 'var(--success-color)',
                                                        fontWeight: 'bold',
                                                        marginRight: '8px'
                                                    }}>
                                                        {task.daysLeft} dias
                                                    </span>
                                                )}
                                                {task.dwellTime !== undefined && (
                                                    <span style={{ marginRight: '8px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                                        {task.dwellTime}h
                                                    </span>
                                                )}
                                                <span className={task.priority === 'critical' || task.priority === 'high' ?
                                                    styles.priorityBadge : styles.categoryBadge}>
                                                    {priorityLabel}
                                                </span>
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    <div className={styles.modalFooter}>
                        <button onClick={() => setFocusMode(false)} className={styles.modalCloseFooterBtn}>
                            Fechar
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ============================================================================
    // Bottleneck Analysis Component
    // ============================================================================
    const BottleneckAnalysis = () => {
        if (bottleneckData.length === 0) {
            return <div className={styles.emptyState}>Nenhum dado de fase disponível para análise de gargalos.</div>;
        }

        const maxDwellTime = Math.max(...bottleneckData.map(b => b.avgDwellTimeMinutes), 1);

        return (
            <div>
                {bottleneckData.map(phase => {
                    const percentage = Math.min((phase.avgDwellTimeMinutes / maxDwellTime) * 100, 100);
                    const isBottleneck = phase.avgDwellTimeMinutes > maxDwellTime * 0.6;

                    return (
                        <div key={phase.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: '8px'
                        }}>
                            <div style={{
                                minWidth: '140px',
                                fontSize: '0.85rem',
                                color: 'var(--text-secondary)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}>
                                {phase.name}
                                <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                                    (n={phase.sampleCount})
                                </span>
                                {isBottleneck && (
                                    <span style={{ fontSize: '0.7rem', color: 'var(--danger-color)' }}>⚠️</span>
                                )}
                            </div>
                            <div style={{
                                flex: 1,
                                height: '10px',
                                backgroundColor: 'var(--bg-tertiary)',
                                borderRadius: '5px',
                                overflow: 'hidden',
                                position: 'relative'
                            }}>
                                <div style={{
                                    width: `${percentage}%`,
                                    height: '100%',
                                    backgroundColor: isBottleneck ? 'var(--danger-color)' :
                                        phase.avgDwellTimeMinutes > maxDwellTime * 0.3 ? 'var(--warning-color)' :
                                            'var(--primary-color)',
                                    borderRadius: '5px',
                                    transition: 'width 0.5s ease'
                                }} />
                            </div>
                            <div style={{
                                minWidth: '70px',
                                fontSize: '0.85rem',
                                textAlign: 'right',
                                fontWeight: isBottleneck ? 600 : 400,
                                color: isBottleneck ? 'var(--danger-color)' : 'var(--text-primary)'
                            }}>
                                {phase.avgDwellTimeMinutes >= 60
                                    ? `${(phase.avgDwellTimeMinutes / 60).toFixed(1)}h`
                                    : `${phase.avgDwellTimeMinutes.toFixed(0)}min`}
                            </div>
                        </div>
                    );
                })}
                <div className={styles.chartInsight} style={{ marginTop: '12px' }}>
                    <FiInfo size={16} />
                    <span>
                        {bottleneckData.filter(b => b.avgDwellTimeMinutes > 60).length > 0
                            ? `⚠️ ${bottleneckData.filter(b => b.avgDwellTimeMinutes > 60).length} fase(s) com tempo médio > 60min - verificar gargalos`
                            : '✅ Todas as fases com tempo médio dentro da normalidade'}
                    </span>
                </div>
            </div>
        );
    };

    // ============================================================================
    // Predictive Alerts Component
    // ============================================================================
    const PredictiveAlerts = () => {
        if (predictiveAlerts.length === 0) {
            return (
                <div className={styles.emptyState}>
                    <FiCheckCircle size={32} style={{ color: 'var(--success-color)' }} />
                    <p>Nenhum alerta preditivo no momento</p>
                    <span>Todos os parâmetros estão dentro da normalidade</span>
                </div>
            );
        }

        return (
            <div className={styles.servicesList}>
                {predictiveAlerts.map((alert, index) => (
                    <div key={index} className={styles.serviceItem}>
                        <span className={styles.serviceIcon}>
                            {alert.type === 'critical' ? '⚠️' : '📊'}
                        </span>
                        <span className={styles.serviceLabel} style={{ fontSize: '0.9rem' }}>
                            {alert.message}
                        </span>
                        <span className={styles.serviceStatus}>
                            <span className={alert.type === 'critical' ? styles.priorityBadge : styles.categoryBadge}
                                style={{
                                    backgroundColor: alert.type === 'critical' ? 'var(--danger-light)' : 'var(--warning-light)',
                                    color: alert.type === 'critical' ? 'var(--danger-color)' : 'var(--warning-color)'
                                }}>
                                {alert.failureRate}% falha
                            </span>
                        </span>
                    </div>
                ))}
            </div>
        );
    };

    // ============================================================================
    // Recent Quality Results Component
    // ============================================================================
    const RecentQualityResults = () => {
        if (recentQualityResults.length === 0) {
            return (
                <div className={styles.emptyState}>
                    <p>Nenhum resultado de qualidade registrado recentemente.</p>
                </div>
            );
        }

        return (
            <div className={styles.ticketsTable}>
                {recentQualityResults.slice(0, 8).map((result, index) => {
                    const statusInfo = QUALITY_STATUS_COLORS[result.status] || QUALITY_STATUS_COLORS['pending'];
                    return (
                        <div key={index} className={styles.ticketRow}>
                            <div className={styles.ticketInfo}>
                                <div className={styles.ticketHeader}>
                                    <strong>{result.productName}</strong>
                                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: '8px' }}>
                                        OP: {result.opCode}
                                    </span>
                                    <span className={styles.categoryBadge} style={{
                                        backgroundColor: 'var(--bg-tertiary)',
                                        color: 'var(--text-secondary)',
                                        fontSize: '0.7rem'
                                    }}>
                                        {result.criteria}
                                    </span>
                                </div>
                                <div className={styles.ticketMeta}>
                                    <span>
                                        {result.date ? timeAgo(result.date) : 'N/A'}
                                    </span>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                                    {result.value || '—'}
                                </span>
                                <span className={styles.priorityBadge} style={{
                                    backgroundColor: statusInfo.bg,
                                    color: statusInfo.color
                                }}>
                                    {statusInfo.label}
                                </span>
                                {result.orderId && (
                                    <button
                                        onClick={() => handleViewOrderDetails(result.orderId)}
                                        className={styles.actionButton}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: 'var(--primary-color)',
                                            padding: '4px 8px'
                                        }}
                                        title="Ver detalhes da OP"
                                    >
                                        <FiEye size={14} />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // ============================================================================
    // Loading State
    // ============================================================================
    if (isLoading && !lastUpdate) {
        return (
            <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <p>Carregando dados do laboratório...</p>
            </div>
        );
    }

    // ============================================================================
    // MAIN RENDER
    // ============================================================================
    return (
        <div className={styles.dashboardContainer}>
            <FocusMode />

            {/* Order Detail Modal */}
            {showOrderDetail && selectedOrderDetails && (
                <div className={styles.modalOverlay} onClick={() => setShowOrderDetail(false)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3>Detalhes da Análise</h3>
                            <button onClick={() => setShowOrderDetail(false)} className={styles.modalCloseBtn}>
                                <FiX size={20} />
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div><strong>Produto:</strong> {selectedOrderDetails.productName}</div>
                                <div><strong>OP:</strong> {selectedOrderDetails.opCode}</div>
                                <div><strong>Critério:</strong> {selectedOrderDetails.criteria}</div>
                                <div><strong>Valor:</strong> {selectedOrderDetails.value || '—'}</div>
                                <div><strong>Status:</strong> {selectedOrderDetails.status}</div>
                                <div><strong>Data:</strong> {selectedOrderDetails.date ? timeAgo(selectedOrderDetails.date) : 'N/A'}</div>
                            </div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button onClick={() => setShowOrderDetail(false)} className={styles.modalCloseFooterBtn}>
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Alerts Modal */}
            {showAlertsModal && (
                <div className={styles.modalOverlay} onClick={() => setShowAlertsModal(false)}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3><FiAlertTriangle size={20} /> Alertas de Qualidade</h3>
                            <button onClick={() => setShowAlertsModal(false)} className={styles.modalCloseBtn}>
                                <FiX size={20} />
                            </button>
                        </div>
                        <div className={styles.modalBody}>
                            <PredictiveAlerts />
                        </div>
                        <div className={styles.modalFooter}>
                            <button onClick={() => setShowAlertsModal(false)} className={styles.modalCloseFooterBtn}>
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className={styles.dashboardHeader}>
                <div className={styles.headerMeta}>
                    <span className={styles.lastUpdate}>
                        <FiClock size={14} />
                        {lastUpdate ? `Última atualização: ${lastUpdate.toLocaleTimeString()}` : 'Carregando...'}
                    </span>
                    <span className={styles.userInfo}>
                        <FiUsers size={14} /> {user?.name || 'Analista'} - {user?.role || 'Laboratório'}
                    </span>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                        onClick={handleRefresh}
                        className={styles.refreshButton}
                        title="Atualizar dados"
                        disabled={isLoading}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px 8px',
                            borderRadius: '4px',
                            color: 'var(--text-secondary)'
                        }}
                    >
                        <FiRefreshCw size={18} className={isLoading ? styles.buttonSpinner : ''} />
                    </button>
                    {urgentTasks.length > 0 && (
                        <button
                            onClick={() => setFocusMode(true)}
                            className={styles.focusButton}
                            style={{
                                padding: '6px 14px',
                                backgroundColor: 'var(--danger-color)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <FiTarget size={14} />
                            {urgentTasks.filter(t => t.priority === 'critical' || t.priority === 'high').length} urgentes
                        </button>
                    )}
                </div>
            </div>

            {/* Metrics Grid - Overview */}
            <div className={styles.metricsGrid}>
                <QualityMetricCard
                    title={t('pages.dashboard.laboratory.pendingTests') || 'Testes Pendentes'}
                    value={pendingTests}
                    subtitle={pendingTests > 5 ? 'Prioridade alta' : 'Em dia'}
                    icon={<FaVial size={20} />}
                    color={{
                        bg: pendingTests > 5 ? 'var(--warning-light)' : 'var(--bg-tertiary)',
                        icon: pendingTests > 5 ? 'var(--warning-color)' : 'var(--primary-color)'
                    }}
                    trend={pendingTests > 0 ? Math.min(pendingTests * 2, 100) : 0}
                />

                <QualityMetricCard
                    title={t('pages.dashboard.laboratory.approvedTests') || 'Testes Aprovados'}
                    value={approvedTests}
                    subtitle={qualityTestStats.rejected > 0 ? `${qualityTestStats.rejected} rejeitados` : 'Nenhuma rejeição'}
                    icon={<FiCheckCircle size={20} />}
                    color={{ bg: 'var(--success-light)', icon: 'var(--success-color)' }}
                />

                <QualityMetricCard
                    title={t('pages.dashboard.laboratory.bottlenecks') || 'Gargalos'}
                    value={bottleneckData.filter(b => b.avgDwellTimeMinutes > 30).length}
                    subtitle={`${bottleneckData.filter(b => b.avgDwellTimeMinutes > 60).length} críticos`}
                    icon={<FiBarChart2 size={20} />}
                    color={{ bg: 'var(--bg-tertiary)', icon: 'var(--primary-color)' }}
                />

                <QualityMetricCard
                    title={t('pages.dashboard.laboratory.expiringMaterials') || 'Lotes Vencendo'}
                    value={expiringMaterials}
                    subtitle={expiringMaterials > 0 ? 'Próximos 30 dias' : 'Nenhum vencendo'}
                    icon={<FiClock size={20} />}
                    color={{
                        bg: expiringMaterials > 0 ? 'var(--danger-light)' : 'var(--bg-tertiary)',
                        icon: expiringMaterials > 0 ? 'var(--danger-color)' : 'var(--text-muted)'
                    }}
                />

                <QualityMetricCard
                    title={t('pages.dashboard.laboratory.activeOrders') || 'Ordens Ativas'}
                    value={activeOrders}
                    subtitle={`${qualityPendingOrders} em CQ`}
                    icon={<MdProductionQuantityLimits size={20} />}
                    color={{ bg: 'var(--bg-tertiary)', icon: 'var(--primary-color)' }}
                />

                <QualityMetricCard
                    title={t('pages.dashboard.laboratory.projects') || 'Projetos Ativos'}
                    value={activeProjects}
                    subtitle={`${developmentProjects.length} total`}
                    icon={<MdScience size={20} />}
                    color={{ bg: 'var(--bg-secondary)', icon: 'var(--primary-color)' }}
                />
            </div>

            {/* Quality Trend Section */}
            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                        <FaChartLine size={20} /> Tendência de Qualidade (30 dias)
                    </h2>
                    <span className={styles.sectionBadge}>
                        {qualityTrend.approved > 80 ? '✅ Excelente' :
                            qualityTrend.approved > 60 ? '⚠️ Atenção' : '🔴 Crítico'}
                    </span>
                </div>
                <div className={styles.card}>
                    <div className={styles.cardContent}>
                        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                            <div style={{ flex: 1, minWidth: '120px' }}>
                                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Aprovações</div>
                                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--success-color)' }}>
                                    {qualityTrend.approved.toFixed(0)}%
                                </div>
                                <div style={{ height: '8px', backgroundColor: 'var(--bg-tertiary)', borderRadius: '4px', marginTop: '4px' }}>
                                    <div style={{
                                        width: `${Math.min(qualityTrend.approved, 100)}%`,
                                        height: '100%',
                                        backgroundColor: 'var(--success-color)',
                                        borderRadius: '4px',
                                        transition: 'width 0.5s ease'
                                    }} />
                                </div>
                            </div>
                            <div style={{ flex: 1, minWidth: '120px' }}>
                                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Rejeições</div>
                                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--danger-color)' }}>
                                    {qualityTrend.rejected.toFixed(0)}%
                                </div>
                                <div style={{ height: '8px', backgroundColor: 'var(--bg-tertiary)', borderRadius: '4px', marginTop: '4px' }}>
                                    <div style={{
                                        width: `${Math.min(qualityTrend.rejected, 100)}%`,
                                        height: '100%',
                                        backgroundColor: 'var(--danger-color)',
                                        borderRadius: '4px',
                                        transition: 'width 0.5s ease'
                                    }} />
                                </div>
                            </div>
                            <div style={{ flex: 1, minWidth: '120px' }}>
                                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Pendentes</div>
                                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--warning-color)' }}>
                                    {qualityTrend.pending.toFixed(0)}%
                                </div>
                                <div style={{ height: '8px', backgroundColor: 'var(--bg-tertiary)', borderRadius: '4px', marginTop: '4px' }}>
                                    <div style={{
                                        width: `${Math.min(qualityTrend.pending, 100)}%`,
                                        height: '100%',
                                        backgroundColor: 'var(--warning-color)',
                                        borderRadius: '4px',
                                        transition: 'width 0.5s ease'
                                    }} />
                                </div>
                            </div>
                            <div style={{ flex: 1, minWidth: '120px' }}>
                                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Total de Testes</div>
                                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--primary-color)' }}>
                                    {qualityTestStats.pending + qualityTestStats.approved + qualityTestStats.rejected}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}>
                                    Últimos 30 dias
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Predictive Alerts Section */}
            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                        <FiAlertTriangle size={20} /> {t('pages.dashboard.laboratory.predictiveAlerts') || 'Alertas Preditivos'}
                    </h2>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span className={styles.sectionBadge}>
                            {qualityAlerts > 0 ? `${qualityAlerts} críticos` : 'Sistema estável'}
                        </span>
                        {predictiveAlerts.length > 5 && (
                            <button
                                onClick={() => setShowAlertsModal(true)}
                                className={styles.actionButton}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: 'var(--primary-color)',
                                    fontSize: '0.8rem'
                                }}
                            >
                                Ver todos
                            </button>
                        )}
                    </div>
                </div>
                <div className={styles.card}>
                    <div className={styles.cardContent}>
                        <PredictiveAlerts />
                    </div>
                </div>
            </div>

            {/* Production Status */}
            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                        <MdProductionQuantityLimits size={20} /> {t('pages.dashboard.laboratory.productionStatus') || 'Status da Produção'}
                    </h2>
                    <span className={styles.sectionBadge}>{activeOrders} ativas</span>
                </div>
                <div className={styles.metricsGrid} style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
                    <div className={styles.metricsCard}>
                        <div className={styles.metricsHeader}>
                            <div className={styles.metricsIcon} style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--primary-color)' }}>
                                <FiPackage size={20} />
                            </div>
                        </div>
                        <div className={styles.metricsValue}>{activeOrders}</div>
                        <div className={styles.metricsLabel}>{t('pages.dashboard.laboratory.activeOrders') || 'Ordens Ativas'}</div>
                        <div className={styles.metricsSubtitle}>Em andamento</div>
                    </div>

                    <div className={styles.metricsCard}>
                        <div className={styles.metricsHeader}>
                            <div className={styles.metricsIcon} style={{ backgroundColor: 'var(--warning-light)', color: 'var(--warning-color)' }}>
                                <FaMicroscope size={20} />
                            </div>
                        </div>
                        <div className={styles.metricsValue}>{qualityPendingOrders}</div>
                        <div className={styles.metricsLabel}>{t('pages.dashboard.laboratory.qualityPending') || 'Aguardando CQ'}</div>
                        <div className={styles.metricsSubtitle}>
                            {qualityPendingOrders > 0 ? '⚠️ Ação necessária' : 'Todos liberados'}
                        </div>
                    </div>

                    <div className={styles.metricsCard}>
                        <div className={styles.metricsHeader}>
                            <div className={styles.metricsIcon} style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--primary-color)' }}>
                                <FiDroplet size={20} />
                            </div>
                        </div>
                        <div className={styles.metricsValue}>{waterMaterials.length}</div>
                        <div className={styles.metricsLabel}>{t('pages.dashboard.laboratory.waterMaterials') || 'Materiais Água'}</div>
                        <div className={styles.metricsSubtitle}>Recursos identificados</div>
                    </div>

                    <div className={styles.metricsCard}>
                        <div className={styles.metricsHeader}>
                            <div className={styles.metricsIcon} style={{
                                backgroundColor: phaseDistribution['phase-4'] || phaseDistribution['phase-5'] ? 'var(--warning-light)' : 'var(--bg-tertiary)',
                                color: phaseDistribution['phase-4'] || phaseDistribution['phase-5'] ? 'var(--warning-color)' : 'var(--text-muted)'
                            }}>
                                <MdTimeline size={20} />
                            </div>
                        </div>
                        <div className={styles.metricsValue}>
                            {Object.entries(phaseDistribution)
                                .filter(([phase]) => ['phase-4', 'phase-5', 'phase-6'].includes(phase))
                                .reduce((sum, [_, count]) => sum + count, 0)}
                        </div>
                        <div className={styles.metricsLabel}>Em CQ</div>
                        <div className={styles.metricsSubtitle}>
                            {Object.entries(phaseDistribution)
                                .filter(([phase]) => ['phase-4', 'phase-5', 'phase-6'].includes(phase))
                                .length > 0 ? 'Análises em andamento' : 'Nenhuma em CQ'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Phase Distribution */}
            {Object.keys(phaseDistribution).length > 0 && (
                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <h2 className={styles.sectionTitle}>
                            <MdShowChart size={20} /> Distribuição por Fase
                        </h2>
                        <span className={styles.sectionBadge}>
                            {Object.values(phaseDistribution).reduce((a, b) => a + b, 0)} ordens ativas
                        </span>
                    </div>
                    <div className={styles.card}>
                        <div className={styles.cardContent}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                                {Object.entries(phaseDistribution)
                                    .sort((a, b) => (PHASE_DISPLAY_NAMES[a[0]] || a[0]).localeCompare(PHASE_DISPLAY_NAMES[b[0]] || b[0]))
                                    .map(([phaseId, count]) => {
                                        const isQualityPhase = ['phase-4', 'phase-5', 'phase-6'].includes(phaseId);
                                        const isWeighingPhase = phaseId === 'phase-1';
                                        const isPackagingPhase = ['phase-7', 'phase-8', 'phase-9', 'phase-10'].includes(phaseId);

                                        let color = 'var(--primary-color)';
                                        let bgColor = 'var(--bg-secondary)';
                                        if (isQualityPhase) {
                                            color = 'var(--warning-color)';
                                            bgColor = 'var(--warning-light)';
                                        } else if (isWeighingPhase) {
                                            color = 'var(--info-color)';
                                            bgColor = 'var(--info-light)';
                                        } else if (isPackagingPhase) {
                                            color = 'var(--success-color)';
                                            bgColor = 'var(--success-light)';
                                        }

                                        return (
                                            <div key={phaseId} style={{
                                                padding: '10px 16px',
                                                backgroundColor: bgColor,
                                                borderRadius: '8px',
                                                borderLeft: `3px solid ${color}`,
                                                minWidth: '120px',
                                                flex: '0 1 auto'
                                            }}>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                                                    {PHASE_DISPLAY_NAMES[phaseId] || phaseId}
                                                </div>
                                                <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color }}>
                                                    {count} {count === 1 ? 'OP' : 'OPs'}
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Bottleneck Analysis Section */}
            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                        <FiBarChart2 size={20} /> {t('pages.dashboard.laboratory.bottleneckAnalysis') || 'Análise de Gargalos'}
                    </h2>
                    <span className={styles.sectionBadge}>Tempo médio por fase</span>
                </div>
                <div className={styles.card}>
                    <div className={styles.cardContent}>
                        <BottleneckAnalysis />
                    </div>
                </div>
            </div>

            {/* Traceability Section */}
            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                        <FiLink size={20} /> {t('pages.dashboard.laboratory.traceability') || 'Rastreabilidade de Lotes'}
                    </h2>
                    <span className={styles.sectionBadge}>Genealogia de produção</span>
                </div>
                <div className={styles.card}>
                    <div className={styles.cardContent}>
                        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                            <input
                                type="text"
                                placeholder={t('pages.dashboard.laboratory.traceabilityPlaceholder') || 'Código do Lote (Ex: MP001 ou LOTE123)'}
                                value={selectedBatch}
                                onChange={(e) => setSelectedBatch(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleTraceabilitySearch(e)}
                                style={{
                                    flex: 1,
                                    padding: '10px 14px',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '4px',
                                    fontSize: '0.95rem',
                                    backgroundColor: 'var(--card-bg)',
                                    color: 'var(--text-primary)',
                                    minWidth: '200px'
                                }}
                            />
                            <button
                                onClick={handleTraceabilitySearch}
                                disabled={traceabilityLoading}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: 'var(--primary-color)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: traceabilityLoading ? 'not-allowed' : 'pointer',
                                    fontSize: '0.95rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    opacity: traceabilityLoading ? 0.7 : 1
                                }}
                            >
                                {traceabilityLoading ? (
                                    <div className={styles.spinner} style={{ width: '16px', height: '16px' }} />
                                ) : (
                                    <FiSearch size={16} />
                                )}
                                {t('common.search') || 'Buscar'}
                            </button>
                            {traceabilityData && !traceabilityData.notFound && (
                                <button
                                    onClick={() => {
                                        setTraceabilityData(null);
                                        setSelectedBatch('');
                                    }}
                                    style={{
                                        padding: '10px 16px',
                                        backgroundColor: 'var(--bg-tertiary)',
                                        color: 'var(--text-secondary)',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '0.9rem'
                                    }}
                                >
                                    <FiX size={16} /> Limpar
                                </button>
                            )}
                        </div>
                        <TraceabilityGraph />
                    </div>
                </div>
            </div>

            {/* UPDATED: Low Stock Materials Section - Only materials under 100kg */}
            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                        <FiPackage size={20} />
                        {t('pages.dashboard.laboratory.materialStock') || 'Materiais com Estoque Baixo'}
                        <span style={{
                            fontSize: '0.75rem',
                            fontWeight: 'normal',
                            color: 'var(--text-muted)',
                            marginLeft: '8px'
                        }}>
                            (&lt; 100 kg)
                        </span>
                    </h2>
                    <span className={styles.sectionBadge} style={{
                        backgroundColor: lowStockMaterials.length > 0 ? 'var(--danger-light)' : 'var(--success-light)',
                        color: lowStockMaterials.length > 0 ? 'var(--danger-color)' : 'var(--success-color)'
                    }}>
                        {lowStockMaterials.length > 0 ? `${lowStockMaterials.length} em alerta` : '✅ Estoque adequado'}
                    </span>
                </div>
                <div className={styles.ticketsTable}>
                    {lowStockMaterials.length === 0 ? (
                        <div className={styles.emptyState}>
                            <FiCheckCircle size={32} style={{ color: 'var(--success-color)' }} />
                            <p>Todos os materiais estão com estoque adequado</p>
                            <span>Nenhum material com menos de 100 kg em estoque</span>
                        </div>
                    ) : (
                        lowStockMaterials.slice(0, 15).map((material, index) => {
                            // Determine urgency level based on quantity
                            let urgencyLevel = 'medium';
                            let urgencyColor = 'var(--warning-color)';
                            let urgencyBg = 'var(--warning-light)';
                            let urgencyLabel = 'Atenção';

                            if (material.quantity <= 20) {
                                urgencyLevel = 'critical';
                                urgencyColor = 'var(--danger-color)';
                                urgencyBg = 'var(--danger-light)';
                                urgencyLabel = '⚠️ Crítico';
                            } else if (material.quantity <= 50) {
                                urgencyLevel = 'high';
                                urgencyColor = 'var(--danger-color)';
                                urgencyBg = 'var(--danger-light)';
                                urgencyLabel = '🔴 Urgente';
                            } else if (material.quantity <= 80) {
                                urgencyLevel = 'medium';
                                urgencyColor = 'var(--warning-color)';
                                urgencyBg = 'var(--warning-light)';
                                urgencyLabel = '🟡 Atenção';
                            } else {
                                urgencyLevel = 'low';
                                urgencyColor = 'var(--info-color)';
                                urgencyBg = 'var(--info-light)';
                                urgencyLabel = '🔵 Monitorar';
                            }

                            return (
                                <div key={material.id || index} className={styles.ticketRow} style={{
                                    borderLeft: `4px solid ${urgencyColor}`,
                                    backgroundColor: urgencyLevel === 'critical' ? 'rgba(229, 62, 62, 0.05)' : 'transparent'
                                }}>
                                    <div className={styles.ticketInfo}>
                                        <div className={styles.ticketHeader}>
                                            <strong>{material.code}</strong>
                                            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                                                {material.name}
                                            </span>
                                            {material.locationCount > 1 && (
                                                <span style={{
                                                    fontSize: '0.65rem',
                                                    padding: '2px 6px',
                                                    backgroundColor: 'var(--bg-tertiary)',
                                                    borderRadius: '4px',
                                                    color: 'var(--text-muted)'
                                                }}>
                                                    {material.locationCount} locais
                                                </span>
                                            )}
                                        </div>
                                        <div className={styles.ticketMeta}>
                                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                                {material.quantity > 0 ? `Estoque em ${material.locationCount} localizações` : 'Sem estoque'}
                                            </span>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <div style={{ textAlign: 'right' }}>
                                            <span style={{
                                                fontWeight: 700,
                                                fontSize: '1.1rem',
                                                color: urgencyLevel === 'critical' ? 'var(--danger-color)' :
                                                    urgencyLevel === 'high' ? 'var(--danger-color)' :
                                                        'var(--warning-color)'
                                            }}>
                                                {material.quantity.toFixed(3)}
                                            </span>
                                            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginLeft: '2px' }}>
                                                {material.unit}
                                            </span>
                                        </div>
                                        <span className={styles.priorityBadge} style={{
                                            backgroundColor: urgencyBg,
                                            color: urgencyColor,
                                            fontWeight: 'bold'
                                        }}>
                                            {urgencyLabel}
                                        </span>
                                    </div>
                                </div>
                            );
                        })
                    )}
                    {lowStockMaterials.length > 15 && (
                        <div style={{
                            padding: '8px 12px',
                            textAlign: 'center',
                            fontSize: '0.8rem',
                            color: 'var(--text-muted)',
                            borderTop: '1px solid var(--border-color)'
                        }}>
                            + {lowStockMaterials.length - 15} mais materiais com estoque baixo
                        </div>
                    )}
                </div>
            </div>

            {/* Recent Quality Results */}
            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h2 className={styles.sectionTitle}>
                        <FiClipboard size={20} /> {t('pages.dashboard.laboratory.recentQuality') || 'Resultados Recentes de Qualidade'}
                    </h2>
                    <span className={styles.sectionBadge}>Últimas análises</span>
                </div>
                <div className={styles.card}>
                    <div className={styles.cardContent}>
                        <RecentQualityResults />
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className={styles.dashboardFooter} style={{
                marginTop: '24px',
                padding: '16px 0',
                borderTop: '1px solid var(--border-color)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '0.8rem',
                color: 'var(--text-muted)'
            }}>
                <span>
                    <FiClock size={12} style={{ marginRight: '4px' }} />
                    Dados em tempo real · {lastUpdate ? lastUpdate.toLocaleString() : 'N/A'}
                </span>
                <span>
                    {pendingTests + approvedTests + qualityTestStats.rejected} testes registrados
                    {qualityAlerts > 0 && ` · ${qualityAlerts} alertas ativos`}
                    {lowStockMaterials.length > 0 && ` · ⚠️ ${lowStockMaterials.length} materiais com estoque baixo`}
                    {traceabilityData && !traceabilityData.notFound && ` · 🔗 Rastreabilidade: ${traceabilityData.material?.code || 'Lote'}`}
                </span>
            </div>
        </div>
    );
};

export default LaboratorioDashboard;